import { useState } from 'react';

let counter = 0;

/** Uma chave só do React para a linha em edição — nunca vai ao servidor. */
export function newRowKey(prefix = 'linha'): string {
  counter += 1;
  return `${prefix}-${counter}`;
}

/**
 * A mecânica de um editor de linhas que salva o conjunto inteiro.
 *
 * Duas telas tinham a mesma máquina, escrita duas vezes: a régua de cobrança
 * (`billing/TiersScreen`) e a de comissão (`sales/LadderScreen`). O que elas
 * têm em comum não é a coluna — é a disciplina:
 *
 * - "há alteração pendente" é a assinatura do que **iria para o servidor**
 *   (`toBody`) comparada com a última gravada, não o estado local comparado
 *   consigo mesmo. Digitar e apagar volta a "sem alteração";
 * - depois de salvar, a tela **re-semeia do que o servidor devolveu**, não do
 *   estado local: a régua gravada é a que ele leu de volta do banco, com a
 *   ordenação e a normalização dele. Manter o local seria a tela afirmando que
 *   gravou o que ela tinha, e não o que ficou lá.
 *
 * O que continua em cada tela: a forma da linha, as colunas, a validação de
 * forma e a mutação. Isto não é um editor genérico — é o que impede as duas
 * réguas de divergirem na primeira correção.
 */
export function useEditableRows<Wire, Row>(
  initial: readonly Wire[],
  toRows: (wire: readonly Wire[]) => Row[],
  toBody: (rows: readonly Row[]) => unknown,
) {
  const [rows, setRows] = useState<Row[]>(() => toRows(initial));
  const [saved, setSaved] = useState<string>(() =>
    JSON.stringify(toBody(toRows(initial))),
  );

  const updated = JSON.stringify(toBody(rows)) !== saved;

  const reseed = (wire: readonly Wire[]) => {
    const next = toRows(wire);
    setRows(next);
    setSaved(JSON.stringify(toBody(next)));
  };

  return { rows, setRows, updated, reseed };
}
